import { useFormContext } from 'react-hook-form';
import { useState, useEffect } from 'react';

import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import SelectTagInput from '@/components/SelectTagInput';
import DatePicker, { DatePickerMode } from '@/components/DatePicker';
import SelectField from '@/components/SelectField';
import { useData } from '@/contexts/data';
import { contextsService } from '@/services/contexts';
import { ContextType } from '@/types/contexts';

import { ContextFormData, PhaseProps } from '@/pages/settings/content/contexts/create/shared/types';

export default function One({ onNext }: PhaseProps) {
  const { register, watch, setValue, formState: { errors } } = useFormContext<ContextFormData>();
  const { territories, territoriesLoading, topics, topicsLoading } = useData();
  const [contextTypes, setContextTypes] = useState<ContextType[]>([]);

  const watchedData = watch();

  useEffect(() => {
    contextsService.getTypes().then((typesData) => {
      setContextTypes(typesData.data || []);
    }).catch((error) => {
      console.error('Error loading data:', error);
    });
  }, []);

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Novo Contexto</h2>
        <p className="text-gray-600">Adicione um novo Contexto Ativável ao Culture Monitor.</p>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Informações Gerais</h3>
          <p className="text-sm text-gray-600 mb-6">Preencha os campos abaixo sobre a atuação da Marca.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome do Contexto
            </label>
            <Input
              {...register('name')}
              placeholder="Nome do contexto"
              className={`w-full ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && (
              <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              ID do Contexto
            </label>
            <Input
              {...register('contextId')}
              placeholder="ID único do contexto"
              className={`w-full ${errors.contextId ? 'border-red-500' : ''}`}
            />
            {errors.contextId && (
              <p className="text-red-500 text-xs mt-1">{errors.contextId.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tipo de Contexto
            </label>
            <SelectField
              value={watchedData.type_id}
              onChange={(value) => setValue('type_id', value, { shouldValidate: true })}
              options={contextTypes.map(type => ({
                value: type.id,
                label: type.name
              }))}
              placeholder="Selecione o tipo"
              hasError={!!errors.type_id}
            />
            {errors.type_id && (
              <p className="text-red-500 text-xs mt-1">{errors.type_id.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Origem do Dado
            </label>
            <div className="flex items-center gap-2 w-full">
              <SelectField
                value={watchedData.dataOrigin}
                onChange={(value) => setValue('dataOrigin', value, { shouldValidate: true })}
                options={[
                  { value: 'api', label: 'API' },
                  { value: 'manual', label: 'Manual' },
                  { value: 'import', label: 'Importação' }
                ]}
                placeholder="Selecione a origem"
                hasError={!!errors.dataOrigin}
                className="flex-1"
              />
              <span className="text-sm text-gray-500 whitespace-nowrap">ID</span>
            </div>
            {errors.dataOrigin && (
              <p className="text-red-500 text-xs mt-1">{errors.dataOrigin.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tópico Associado
            </label>
            <SelectTagInput
              value={watchedData.associatedTopic || []}
              onChange={(tags) => {
                setValue('associatedTopic', tags, { shouldValidate: true });
              }}
              placeholder="Procure por ID e Selecione"
              showSearchIcon={true}
              items={topics.map(topic => ({
                id: topic.id,
                name: topic.name
              }))}
              loading={topics.length === 0}
            />
            {errors.associatedTopic && (
              <p className="text-red-500 text-xs mt-1">{errors.associatedTopic.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Território Associado
            </label>
            <SelectTagInput
              value={watchedData.associatedTerritory || []}
              onChange={(tags) => {
                setValue('associatedTerritory', tags, { shouldValidate: true });
              }}
              placeholder="Procure por ID e Selecione"
              showSearchIcon={true}
              items={territories.map(territory => ({
                id: territory.id,
                name: territory.name
              }))}
              loading={territoriesLoading}
            />
            {errors.associatedTerritory && (
              <p className="text-red-500 text-xs mt-1">{errors.associatedTerritory.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Palavras-Chave Fixas
            </label>
            <SelectTagInput
              value={watchedData.fixedKeywords || []}
              onChange={(tags) => {
                setValue('fixedKeywords', tags, { shouldValidate: true });
              }}
              placeholder='Digite as palavras-chave e aperte "ok"'
              showSearchIcon={true}
              items={[]}
              loading={false}
            />
            {errors.fixedKeywords && (
              <p className="text-red-500 text-xs mt-1">{errors.fixedKeywords.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Palavras-Chave Dinâmicas
            </label>
            <SelectTagInput
              value={watchedData.dynamicKeywords || []}
              onChange={(tags) => {
                setValue('dynamicKeywords', tags, { shouldValidate: true });
              }}
              placeholder='Digite as palavras-chave e aperte "ok"'
              showSearchIcon={true}
              items={[]}
              loading={false}
            />
            {errors.dynamicKeywords && (
              <p className="text-red-500 text-xs mt-1">{errors.dynamicKeywords.message}</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Início
            </label>
            <DatePicker
              value={watchedData.startDate}
              onChange={(date) => {
                const dateValue = date instanceof Date ? date : (date && 'from' in date ? date.from : undefined)
                setValue('startDate', dateValue || new Date(), { shouldValidate: true })
              }}
              hasError={!!errors.startDate}
              mode={DatePickerMode.Single}
            />
            {errors.startDate && (
              <p className="text-red-500 text-xs mt-1">{errors.startDate.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data de Fim
            </label>
            <DatePicker
              value={watchedData.endDate}
              onChange={(date) => {
                const dateValue = date instanceof Date ? date : (date && 'from' in date ? date.from : undefined)
                setValue('endDate', dateValue || new Date(), { shouldValidate: true })
              }}
              hasError={!!errors.endDate}
              mode={DatePickerMode.Single}
            />
            {errors.endDate && (
              <p className="text-red-500 text-xs mt-1">{errors.endDate.message}</p>
            )}
          </div>

          <div className="w-full">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Frequência
            </label>
            <SelectField
              value={watchedData.frequency}
              onChange={(value) => setValue('frequency', value, { shouldValidate: true })}
              options={[
                { value: 'DAILY', label: 'Diário' },
                { value: 'WEEKLY', label: 'Semanal' },
                { value: 'MONTHLY', label: 'Mensal' },
                { value: 'SEMESTER', label: 'Semestral' },
                { value: 'ANNUAL', label: 'Anual' },
                { value: 'SPORADIC', label: 'Esporádico' }
              ]}
              placeholder="Selecione a frequência"
              hasError={!!errors.frequency}
            />
            {errors.frequency && (
              <p className="text-red-500 text-xs mt-1">{errors.frequency.message}</p>
            )}
          </div>
        </div>

      </div>

      <div className="flex justify-end pt-6">
        <Button
          onClick={onNext}
          className="bg-gray-200 text-gray-800 hover:bg-gray-300 flex items-center gap-2"
        >
          Próximo
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>
    </div>
  );
}
